import React, { Fragment, useRef, useState } from 'react';
import { Transition } from '@headlessui/react';
import OpenNavIcon from '@/shared/icons/OpenNavIcon';
import CloseIcon from '@/shared/icons/CloseIcon';
import StarIcon from '@/shared/icons/StarIcon';
import ChevronDownIcon from '@/shared/icons/ChevronDownIcon';
import ResetIcon from '@/shared/icons/ResetIcon';
import { useConversationStore } from '@/hooks/useConversationStore';

const SlidingChatSidebar = ({ isOpen, onClose, onMinimize, onReset, children, kbId = 'ca7ac107-9716-4ef3-a6b5-55ceab49f15a' }) => {
  const iframeRef = useRef(null);
  const [isResetting, setIsResetting] = useState(false);

  // Use the conversation store to reset conversation
  const { resetCurrentConversation } = useConversationStore(kbId, 'embed');

  const handleReset = async () => {
    setIsResetting(true);
    try {
      // Reset the conversation in the store
      resetCurrentConversation();

      // Call parent's onReset callback to clear the initial conversation ID ref
      // and update the reset timestamp (which will change the iframe URL and force reload)
      if (onReset) {
        onReset();
      }
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <Transition
      show={isOpen}
      as={Fragment}
      enter="transform transition ease-in-out duration-500"
      enterFrom="translate-x-full"
      enterTo="translate-x-0"
      leave="transform transition ease-in-out duration-500"
      leaveFrom="translate-x-0"
      leaveTo="translate-x-full"
    >
      <div className="fixed top-0 right-0 h-[100dvh] w-full sm:w-[390px] bg-white border-l border-grey-5 shadow-lg z-50">
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between px-4 py-5 border-b border-grey-5">
            <div className="flex items-center gap-2">
              <StarIcon className="w-6 h-6 new-text-accent" />
              <span className="font-bold text-base text-grey-75">Assistant</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleReset}
                disabled={isResetting}
                className="rounded-md text-grey-60 hover:text-grey-80 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                aria-label="Reset conversation"
                title="Reset conversation"
              >
                <ResetIcon className="h-4 w-4 text-grey-75" />
              </button>
              <button
                onClick={onMinimize}
                className="rounded-md text-grey-60 hover:text-grey-80 focus:outline-none"
                aria-label="Minimize"
              >
                <ChevronDownIcon className="h-3 w-3 text-grey-75" />
              </button>
              <button
                onClick={onClose}
                className="rounded-md text-grey-60 hover:text-grey-80 focus:outline-none"
                aria-label="Close"
              >
                <CloseIcon className="h-5 w-5 text-black font-bold" />
              </button>
            </div>
          </div>
          <div className="relative flex-1 overflow-y-auto">
            {React.cloneElement(children, {
              ref: iframeRef
            })}
          </div>
        </div>
      </div>
    </Transition>
  );
};

export default SlidingChatSidebar; 